/**
 * The Errors tab's numbers from the report (19/10/2026): today's count and
 * open ones, the critical ones, the refused sends, the read failures, how
 * many of today's alert-worthy rows Slack heard, the last error, how many
 * times today's errors actually happened (repeats) — and the rows narrowed
 * to a campaign group or to the kinds picked.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenErrorRow, DropGenErrorsReport } from '@trafficpulse/shared';

import { isCritical, isRead } from './errors-words';

export interface ErrorFacts {
  today: number;
  open: number;
  /** 21/10/2026 — today's errors fixed since (texts filled, a step that ran again). */
  resolved: number;
  critical: number;
  sends: number;
  reads: number;
  /** Rows that should have reached Slack today, and how many did / failed. */
  slack: { sent: number; due: number; failed: number };
  last: DropGenErrorRow | null;
  happened: number;
}

/** The trace-only kinds never go to Slack: the channel that failed, the read failures judged with less. */
export const alertWorthy = (row: DropGenErrorRow) => row.kind !== 'notification_failed' && !isRead(row.kind);

/** YYYY-MM-DD of an instant on the country's clock (Danish when the country has none). */
export const dayOf = (iso: string, tz: string | null): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: tz ?? 'Europe/Copenhagen', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(iso));

export const isToday = (row: DropGenErrorRow, report: Pick<DropGenErrorsReport, 'to'>, tz: string | null) => (row.runDate ?? dayOf(row.at, tz)) === report.to;

export function errorFacts(report: Pick<DropGenErrorsReport, 'to'>, rows: DropGenErrorRow[], tz: string | null): ErrorFacts {
  const today = rows.filter((r) => isToday(r, report, tz));
  const due = today.filter(alertWorthy);
  return {
    today: today.length,
    open: today.filter((r) => !r.resolvedAt).length,
    resolved: today.filter((r) => r.resolvedAt).length,
    // a critical error that is resolved needs no person any more
    critical: today.filter((r) => isCritical(r.kind) && !r.resolvedAt).length,
    sends: today.filter((r) => r.kind === 'send_failed' || r.kind === 'send_unknown').length,
    reads: today.filter((r) => isRead(r.kind)).length,
    slack: { sent: due.filter((r) => r.notifiedAt).length, due: due.length, failed: due.filter((r) => !r.notifiedAt && r.notifyError).length },
    last: rows[0] ?? null,
    happened: today.reduce((n, r) => n + r.repeats, 0),
  };
}

/** A campaign group's page shows its containers' errors and the ones with no container (country-wide, system-wide). */
export const scoped = (rows: DropGenErrorRow[], scopeIds: ReadonlySet<string> | null) => (scopeIds ? rows.filter((r) => !r.containerId || scopeIds.has(r.containerId)) : rows);

export const filterRows = (rows: DropGenErrorRow[], kinds: ReadonlySet<string>) => (kinds.size === 0 ? rows : rows.filter((r) => kinds.has(r.kind)));
